import React, { useState, useEffect, useCallback } from 'react'
import { X, RotateCcw, RotateCw, Crop, Save, Tag, Plus, Download } from 'lucide-react'
import { satsueiDataTorokuService } from '../../services/satsueiDataTorokuService'
import { useApiError } from '../../../shared/hooks/useApiError'
import MessageDialog from '../../../shared/ui/MessageDialog'

const ImageEditModal = ({ isOpen, onClose, image, objectId, onImageUpdate }) => {
  // Add useApiError hook
  const {
    showErrorDialog,
    errorMessage,
    messageType,
    handleApiError,
    handleApiResponse,
    closeErrorDialog,
    showSuccessMessage
  } = useApiError()

  const [isLoading, setIsLoading] = useState(false)
  const [isRotating, setIsRotating] = useState(false)
  const [imageUrl, setImageUrl] = useState('')
  const [imageBlob, setImageBlob] = useState(null)
  const [rotation, setRotation] = useState(0)
  const [tags, setTags] = useState([])
  const [newTag, setNewTag] = useState('')
  const [availableTags, setAvailableTags] = useState([])
  const [isTrimming, setIsTrimming] = useState(false)
  const [cropArea, setCropArea] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0, offsetX: 0, offsetY: 0 })
  const [hasSelectedCrop, setHasSelectedCrop] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [resizeHandle, setResizeHandle] = useState(null)
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, cropArea: null })

  const loadImageData = useCallback(async () => {
    if (!image || !objectId) return

    setIsLoading(true)
    try {
      // Fetch image as blob
      const response = await satsueiDataTorokuService.getImageBlobForEdit(
        image.objectId,
        image.objectImageId,
        image.objectImageEditId,
        image.filenameExtension
      )
      
      // Create blob URL for display
      const blobUrl = URL.createObjectURL(response.data)
      setImageUrl(blobUrl)
      setImageBlob(response.data)

      // Load image tags
      const tagResult = await satsueiDataTorokuService.getObjectImageTag(
        image.objectId,
        image.objectImageId
      )
      // Ensure all tags have deleteFlg property set to 0 if not present
      const tagsWithDeleteFlg = (tagResult.imageTagList || []).map(tag => ({
        ...tag,
        deleteFlg: (tag.deleteFlg === 0 || tag.deleteFlg === 1) ? tag.deleteFlg : 0,
      }))
      setTags(tagsWithDeleteFlg)

      // Load available tags
      const availableTagsResult = await satsueiDataTorokuService.getImageTagCategoryListByImageTag(
        '', // satsueiMemoCategoryId
        '' // inputImageTag
      )
      const allTags = []
      availableTagsResult.imageTagCategoryList?.forEach(category => {
        category.imageTagList?.forEach(tag => {
          if (!allTags.includes(tag)) {
            allTags.push(tag)
          }
        })
      })
      setAvailableTags(allTags)
    } catch (error) {
      console.error('Failed to load image data:', error)
    } finally {
      setIsLoading(false)
    }
  }, [image, objectId])

  useEffect(() => {
    if (isOpen && image) {
      loadImageData()
    }
  }, [isOpen, image, loadImageData])
  
  const rotateImage = (blob, angle) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()
      
      img.onload = () => {
        // Calculate new canvas dimensions based on rotation
        if (angle === 90 || angle === 270 || angle === -90 || angle === -270) {
          canvas.width = img.height
          canvas.height = img.width
        } else {
          canvas.width = img.width
          canvas.height = img.height
        }
        
        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        
        // Apply rotation
        ctx.translate(canvas.width / 2, canvas.height / 2)
        ctx.rotate((angle * Math.PI) / 180)
        ctx.translate(-img.width / 2, -img.height / 2)
        
        // Draw the rotated image
        ctx.drawImage(img, 0, 0)
        
        // Convert canvas to blob
        canvas.toBlob((blob) => {
          resolve(blob)
        }, 'image/jpeg', 0.9)
      }
      
      img.src = URL.createObjectURL(blob)
    })
  }

  const handleRotate = async (direction) => {
    if (!imageBlob) return
    
    setIsRotating(true)
    try {
      const newRotation = direction === 'left' ? rotation - 90 : rotation + 90
      const rotatedBlob = await rotateImage(imageBlob, newRotation - rotation)
      
      // Update blob and create new URL
      setImageBlob(rotatedBlob)
      const newBlobUrl = URL.createObjectURL(rotatedBlob)
      setImageUrl(newBlobUrl)
      setRotation(newRotation)
    } catch (error) {
      console.error('Failed to rotate image:', error)
      handleApiError(error, '画像の回転に失敗しました')
    } finally {
      setIsRotating(false)
    }
  }

  const handleAddTag = () => {
    if (newTag.trim()) {
      // Check if tag already exists (including deleted ones)
      const existingTag = tags.find(tag => tag.imageTag === newTag.trim())
      
      if (existingTag) {
        // If tag exists and is deleted, restore it
        if (existingTag.deleteFlg === 1) {
          setTags(tags.map(tag => 
            tag.imageTagId === existingTag.imageTagId 
              ? { ...tag, deleteFlg: 0 }
              : tag
          ))
        }
        // If tag exists and is not deleted, do nothing
      } else {
        // Add new tag
      const maxId = Math.max(0, ...tags.map(tag => tag.imageTagId))
      setTags([...tags, {
        imageTagId: maxId + 1,
        imageTag: newTag.trim(),
        deleteFlg: 0,
      }])
      }
      setNewTag('')
    }
  }

  const handleRemoveTag = (tagId) => {
    setTags(tags.map(tag => 
      tag.imageTagId === tagId 
        ? { ...tag, deleteFlg: 1 }
        : tag
    ))
  }

  const handleSaveImageTags = async () => {
    if (!image || !objectId) return

    setIsLoading(true)
    try {
      // Get only active tags (not deleted)
      const activeTags = tags.filter(tag => tag.deleteFlg === 0)

      // Send all active tags - the API will handle reconstruction
      const response = await satsueiDataTorokuService.addObjectImageTag({
        objectId: image.objectId,
        objectImageId: image.objectImageId,
        imageTagList: activeTags
      })

      // Check if response has error
      if (handleApiResponse(response)) {
        return // Error was handled, exit early
      }

      showSuccessMessage('画像タグの保存が完了しました。')
      onImageUpdate()
    } catch (error) {
      console.error('Failed to save image tags:', error)
      handleApiError(error, '画像タグの保存に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveEditedImage = async () => {
    if (!image || !objectId || !imageBlob) return
    
    setIsLoading(true)
    try {
      // Get max object image edit id
      const checkMaxObjectImageEditId = await satsueiDataTorokuService.getMaxImageEditId(
        image.objectId,
        image.objectImageId
      )
      const objectImageEditId = checkMaxObjectImageEditId.objectImageEditId + 1

      // Convert blob to File
      const fileName = `${new Date().getTime()}.${image.filenameExtension}`
      const file = new File([imageBlob], fileName, { type: 'image/jpeg' })

      // Create FormData for upload
      const formData = new FormData()
      formData.append('objectId', image.objectId)
      formData.append('objectImageId', image.objectImageId)
      formData.append('inputFile', file)
      formData.append('fileName', fileName)
      formData.append('fileSize', file.size)
      formData.append('imageFolderId', image.imageFolderId)
      formData.append('objectImageEditId', objectImageEditId)

      // Upload the edited image
      const response = await satsueiDataTorokuService.uploadEditedImage(formData)
      
      // Check if response has error
      if (handleApiResponse(response)) {
        return // Error was handled, exit early
      }
      
      // Show success dialog
      showSuccessMessage('編集画像の保存が完了しました。')
      
      onImageUpdate()
      // onClose()
    } catch (error) {
      console.error('Failed to save edited image:', error)
      handleApiError(error, '編集画像の保存に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownloadImage = () => {
    if (!imageUrl) return

    // Create a temporary link element to trigger download
    const link = document.createElement('a')
    link.href = imageUrl
    link.download = `${image.objectId}_${image.objectImageId}.${image.filenameExtension}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const calculateImageDimensions = (imgElement, containerElement) => {
    const containerRect = containerElement.getBoundingClientRect()
    const imgRect = imgElement.getBoundingClientRect()
    
    // Calculate the actual image position within the container
    const offsetX = imgRect.left - containerRect.left
    const offsetY = imgRect.top - containerRect.top
    
    return {
      width: imgRect.width,
      height: imgRect.height,
      offsetX,
      offsetY
    }
  }

  const handleStartTrimming = () => {
    setIsTrimming(true)
    setCropArea(null)
    setHasSelectedCrop(false)
  }

  const handleCancelTrimming = () => {
    setIsTrimming(false)
    setCropArea(null)
    setIsDragging(false)
  }

  const handleMouseDown = (e) => {
    if (!isTrimming) return
    
    const container = e.currentTarget
    const img = container.querySelector('img')
    if (!img) return
    
    // Calculate image dimensions relative to container
    const dimensions = calculateImageDimensions(img, container)
    setImageDimensions(dimensions)
    
    const rect = container.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    
    // Check if click is within image bounds
    if (x < dimensions.offsetX || x > dimensions.offsetX + dimensions.width ||
        y < dimensions.offsetY || y > dimensions.offsetY + dimensions.height) {
      return
    }
    
    setIsDragging(true)
    setDragStart({ x, y })
    setCropArea({ x, y, width: 0, height: 0 })
  }

  const handleMouseMove = (e) => {
    if (!isTrimming) return
    
    // Handle resizing
    if (isResizing) {
      handleResizeMove(e)
      return
    }
    
    // Handle initial crop selection
    if (!isDragging) return
    
    const container = e.currentTarget
    const rect = container.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    
    // Constrain to image bounds
    const constrainedX = Math.max(imageDimensions.offsetX, Math.min(x, imageDimensions.offsetX + imageDimensions.width))
    const constrainedY = Math.max(imageDimensions.offsetY, Math.min(y, imageDimensions.offsetY + imageDimensions.height))
    
    const startX = Math.max(imageDimensions.offsetX, Math.min(dragStart.x, imageDimensions.offsetX + imageDimensions.width))
    const startY = Math.max(imageDimensions.offsetY, Math.min(dragStart.y, imageDimensions.offsetY + imageDimensions.height))
    
    const width = constrainedX - startX
    const height = constrainedY - startY
    
    setCropArea({
      x: Math.min(startX, constrainedX),
      y: Math.min(startY, constrainedY),
      width: Math.abs(width),
      height: Math.abs(height)
    })
  }

  const handleMouseUp = () => {
    setIsDragging(false)
    setIsResizing(false)
    setResizeHandle(null)
    if (cropArea && cropArea.width > 0 && cropArea.height > 0) {
      setHasSelectedCrop(true)
    }
  }

  const handleResizeStart = (e, handle) => {
    e.stopPropagation()
    setIsResizing(true)
    setResizeHandle(handle)
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      cropArea: { ...cropArea }
    })
  }

  const handleResizeMove = (e) => {
    if (!isResizing || !resizeHandle || !cropArea) return
    
    const deltaX = e.clientX - resizeStart.x
    const deltaY = e.clientY - resizeStart.y
    
    let newCropArea = { ...resizeStart.cropArea }
    
    // Constrain to image bounds
    const minX = imageDimensions.offsetX
    const minY = imageDimensions.offsetY
    const maxX = imageDimensions.offsetX + imageDimensions.width
    const maxY = imageDimensions.offsetY + imageDimensions.height
    
    switch (resizeHandle) {
      case 'nw': // Top-left
        newCropArea.x = Math.max(minX, Math.min(newCropArea.x + deltaX, newCropArea.x + newCropArea.width - 10))
        newCropArea.y = Math.max(minY, Math.min(newCropArea.y + deltaY, newCropArea.y + newCropArea.height - 10))
        newCropArea.width = resizeStart.cropArea.x + resizeStart.cropArea.width - newCropArea.x
        newCropArea.height = resizeStart.cropArea.y + resizeStart.cropArea.height - newCropArea.y
        break
      case 'ne': // Top-right
        newCropArea.y = Math.max(minY, Math.min(newCropArea.y + deltaY, newCropArea.y + newCropArea.height - 10))
        newCropArea.width = Math.max(10, Math.min(newCropArea.width + deltaX, maxX - newCropArea.x))
        newCropArea.height = resizeStart.cropArea.y + resizeStart.cropArea.height - newCropArea.y
        break
      case 'sw': // Bottom-left
        newCropArea.x = Math.max(minX, Math.min(newCropArea.x + deltaX, newCropArea.x + newCropArea.width - 10))
        newCropArea.width = resizeStart.cropArea.x + resizeStart.cropArea.width - newCropArea.x
        newCropArea.height = Math.max(10, Math.min(newCropArea.height + deltaY, maxY - newCropArea.y))
        break
      case 'se': // Bottom-right
        newCropArea.width = Math.max(10, Math.min(newCropArea.width + deltaX, maxX - newCropArea.x))
        newCropArea.height = Math.max(10, Math.min(newCropArea.height + deltaY, maxY - newCropArea.y))
        break
      case 'n': // Top
        newCropArea.y = Math.max(minY, Math.min(newCropArea.y + deltaY, newCropArea.y + newCropArea.height - 10))
        newCropArea.height = resizeStart.cropArea.y + resizeStart.cropArea.height - newCropArea.y
        break
      case 's': // Bottom
        newCropArea.height = Math.max(10, Math.min(newCropArea.height + deltaY, maxY - newCropArea.y))
        break
      case 'w': // Left
        newCropArea.x = Math.max(minX, Math.min(newCropArea.x + deltaX, newCropArea.x + newCropArea.width - 10))
        newCropArea.width = resizeStart.cropArea.x + resizeStart.cropArea.width - newCropArea.x
        break
      case 'e': // Right
        newCropArea.width = Math.max(10, Math.min(newCropArea.width + deltaX, maxX - newCropArea.x))
        break
    }
    
    setCropArea(newCropArea)
  }

  const cropImage = (blob, cropArea, imageDimensions) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()
      
      img.onload = () => {
        // Calculate the actual crop coordinates relative to the original image
        const scaleX = img.width / imageDimensions.width
        const scaleY = img.height / imageDimensions.height
        
        const cropX = (cropArea.x - imageDimensions.offsetX) * scaleX
        const cropY = (cropArea.y - imageDimensions.offsetY) * scaleY
        const cropWidth = cropArea.width * scaleX
        const cropHeight = cropArea.height * scaleY
        
        canvas.width = cropWidth
        canvas.height = cropHeight
        
        ctx.drawImage(
          img,
          cropX, cropY, cropWidth, cropHeight,
          0, 0, cropWidth, cropHeight
        )
        
        // Convert canvas to blob
        canvas.toBlob((blob) => {
          resolve(blob)
        }, 'image/jpeg', 0.9)
      }
      
      img.src = URL.createObjectURL(blob)
    })
  }

  const handleConfirmTrimming = async () => {
    if (!cropArea || !imageBlob || !imageDimensions.width) return
    
    setIsLoading(true)
    try {
      const croppedBlob = await cropImage(imageBlob, cropArea, imageDimensions)
      
      // Update blob and create new URL
      setImageBlob(croppedBlob)
      const newBlobUrl = URL.createObjectURL(croppedBlob)
      setImageUrl(newBlobUrl)
      
      setIsTrimming(false)
      setCropArea(null)
      setHasSelectedCrop(false)
    } catch (error) {
      console.error('Failed to crop image:', error)
      handleApiError(error, '画像の切り抜きに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    // Clean up blob URLs to prevent memory leaks
    if (imageUrl && imageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imageUrl)
    }
    
    // Close any open dialogs
    closeErrorDialog()
    
    // Reset all states
    setRotation(0)
    setTags([])
    setNewTag('')
    setIsTrimming(false)
    setCropArea(null)
    setIsDragging(false)
    setDragStart({ x: 0, y: 0 })
    setImageDimensions({ width: 0, height: 0, offsetX: 0, offsetY: 0 })
    setHasSelectedCrop(false)
    setIsResizing(false)
    setResizeHandle(null)
    setResizeStart({ x: 0, y: 0, cropArea: null })
    setImageBlob(null)
    setIsRotating(false)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4" data-testid="image-edit-modal">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-6xl h-5/6 max-h-[90vh] min-h-[600px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">画像編集</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="modal-close-button"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 flex flex-col lg:flex-row min-h-0">
          {/* Left Panel - Image Display */}
          <div className="flex-1 p-2 sm:p-4 overflow-hidden min-h-0 lg:min-h-[400px]">
            <div className="bg-gray-100 rounded-lg p-2 sm:p-4 h-full flex items-center justify-center overflow-hidden min-h-0 relative">
              {isLoading ? (
                <div className="text-gray-500">読み込み中...</div>
              ) : imageUrl ? (
                <div 
                  className="w-full h-full flex items-center justify-center overflow-hidden min-h-0 relative"
                  style={{ maxHeight: '100%' }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                >
                  <img
                    src={imageUrl}
                    alt="編集対象画像"
                    className="max-w-full max-h-full object-contain select-none"
                    style={{ 
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      display: 'block'
                    }}
                    draggable={false}
                    data-testid="edit-image"
                  />
                  
                  {/* Crop overlay */}
                  {isTrimming && cropArea && (
                    <div
                      className="absolute border-2 border-red-500 bg-red-500 bg-opacity-20"
                      style={{
                        left: cropArea.x,
                        top: cropArea.y,
                        width: cropArea.width,
                        height: cropArea.height,
                        pointerEvents: hasSelectedCrop ? 'auto' : 'none'
                      }}
                    >
                      {/* Resize handles - only show when crop is selected */}
                      {hasSelectedCrop && (
                        <>
                          {/* Corner handles */}
                          <div
                            className="absolute w-3 h-3 bg-red-500 border border-white cursor-nw-resize"
                            style={{ left: -6, top: -6 }}
                            onMouseDown={(e) => handleResizeStart(e, 'nw')}
                          />
                          <div
                            className="absolute w-3 h-3 bg-red-500 border border-white cursor-ne-resize"
                            style={{ right: -6, top: -6 }}
                            onMouseDown={(e) => handleResizeStart(e, 'ne')}
                          />
                          <div
                            className="absolute w-3 h-3 bg-red-500 border border-white cursor-sw-resize"
                            style={{ left: -6, bottom: -6 }}
                            onMouseDown={(e) => handleResizeStart(e, 'sw')}
                          />
                          <div
                            className="absolute w-3 h-3 bg-red-500 border border-white cursor-se-resize"
                            style={{ right: -6, bottom: -6 }}
                            onMouseDown={(e) => handleResizeStart(e, 'se')}
                          />
                          
                          {/* Edge handles */}
                          <div
                            className="absolute w-3 h-3 bg-red-500 border border-white cursor-n-resize"
                            style={{ left: '50%', top: -6, transform: 'translateX(-50%)' }}
                            onMouseDown={(e) => handleResizeStart(e, 'n')}
                          />
                          <div
                            className="absolute w-3 h-3 bg-red-500 border border-white cursor-s-resize"
                            style={{ left: '50%', bottom: -6, transform: 'translateX(-50%)' }}
                            onMouseDown={(e) => handleResizeStart(e, 's')}
                          />
                          <div
                            className="absolute w-3 h-3 bg-red-500 border border-white cursor-w-resize"
                            style={{ left: -6, top: '50%', transform: 'translateY(-50%)' }}
                            onMouseDown={(e) => handleResizeStart(e, 'w')}
                          />
                          <div
                            className="absolute w-3 h-3 bg-red-500 border border-white cursor-e-resize"
                            style={{ right: -6, top: '50%', transform: 'translateY(-50%)' }}
                            onMouseDown={(e) => handleResizeStart(e, 'e')}
                          />
                        </>
                      )}
                    </div>
                  )}
                  
                  {/* Trimming mode overlay */}
                  {isTrimming && (
                    <div className="absolute inset-0 pointer-events-none">
                      {/* Dimmed areas outside the crop selection */}
                      {hasSelectedCrop && cropArea && (
                        <>
                          {/* Top dimmed area */}
                          {cropArea.y > 0 && (
                            <div
                              className="absolute bg-black bg-opacity-50"
                              style={{
                                left: 0,
                                top: 0,
                                width: '100%',
                                height: cropArea.y
                              }}
                            />
                          )}
                          {/* Bottom dimmed area */}
                          <div
                            className="absolute bg-black bg-opacity-50"
                            style={{
                              left: 0,
                              top: cropArea.y + cropArea.height,
                              width: '100%',
                              height: '100%'
                            }}
                          />
                          {/* Left dimmed area */}
                          {cropArea.x > 0 && (
                            <div
                              className="absolute bg-black bg-opacity-50"
                              style={{
                                left: 0,
                                top: cropArea.y,
                                width: cropArea.x,
                                height: cropArea.height
                              }}
                            />
                          )}
                          {/* Right dimmed area */}
                          <div
                            className="absolute bg-black bg-opacity-50"
                            style={{
                              left: cropArea.x + cropArea.width,
                              top: cropArea.y,
                              width: '100%',
                              height: cropArea.height
                            }}
                          />
                        </>
                      )}
                      
                      {/* Instruction text - only show when no crop is selected */}
                      {!hasSelectedCrop && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-white bg-opacity-90 px-4 py-2 rounded-lg text-sm text-gray-700">
                            切り抜き範囲をドラッグして選択してください
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-gray-500">画像を読み込めませんでした</div>
              )}
            </div>
          </div>

          {/* Right Panel - Controls */}
          <div className="w-full lg:w-80 p-2 sm:p-4 border-t lg:border-t-0 lg:border-l border-gray-200 flex flex-col lg:max-h-full overflow-y-auto">
            {/* Rotation Controls */}
            <div className="mb-4 sm:mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-2 sm:mb-3">回転</h4>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleRotate('left')}
                  disabled={isRotating}
                  className="flex-1 px-2 sm:px-3 py-2 sm:py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  data-testid="rotate-left-button"
                >
                  <RotateCcw size={16} className="mx-auto" />
                </button>
                <button
                  onClick={() => handleRotate('right')}
                  disabled={isRotating}
                  className="flex-1 px-2 sm:px-3 py-2 sm:py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  data-testid="rotate-right-button"
                >
                  <RotateCw size={16} className="mx-auto" />
                </button>
              </div>
            </div>

            {/* Trimming Controls */}
            <div className="mb-4 sm:mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-2 sm:mb-3">切り抜き</h4>
              {!isTrimming ? (
              <button
                  onClick={handleStartTrimming}
                className="w-full px-2 sm:px-3 py-2 sm:py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm sm:text-base"
                data-testid="start-trim-button"
              >
                <Crop size={16} className="mx-auto" />
              </button>
              ) : (
                <div className="space-y-2">
                  <button
                    onClick={handleConfirmTrimming}
                    disabled={!cropArea || isLoading}
                    className="w-full px-2 sm:px-3 py-2 sm:py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 text-sm sm:text-base"
                    data-testid="confirm-trim-button"
                  >
                    決定
                  </button>
                  <button
                    onClick={handleCancelTrimming}
                    className="w-full px-2 sm:px-3 py-2 sm:py-3 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm sm:text-base"
                    data-testid="cancel-trim-button"
                  >
                    キャンセル
                  </button>
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-700 mb-3">タグ</h4>
              
              {/* Add Tag */}
              <div className="flex space-x-2 mb-3">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="新しいタグ"
                  className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                  id="tag-input"
                  data-testid="tag-input"
                />
                <button
                  onClick={handleAddTag}
                  className="px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  data-testid="add-tag-button"
                >
                  <Plus size={12} />
                </button>
              </div>

              {/* Tag List */}
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {tags.filter(tag => tag.deleteFlg !== 1).map((tag) => (
                  <div
                    key={tag.imageTagId}
                    className="flex items-center justify-between p-2 bg-gray-100 rounded-md"
                    data-testid={`tag-item-${tag.imageTagId}`}
                  >
                    <span className="text-sm text-gray-700">{tag.imageTag}</span>
                    <button
                      onClick={() => handleRemoveTag(tag.imageTagId)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                      data-testid={`remove-tag-button-${tag.imageTagId}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>

              {/* Available Tags */}
              {availableTags.length > 0 && (
                <div className="mt-4">
                  <h5 className="text-xs font-medium text-gray-600 mb-2">利用可能なタグ</h5>
                  <div className="flex flex-wrap gap-1">
                    {availableTags.slice(0, 10).map((tag, index) => (
                      <button
                        key={index}
                        onClick={() => setNewTag(tag)}
                        className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                        data-testid={`available-tag-button-${index}`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="mt-4 space-y-2">
              <button
                onClick={handleSaveImageTags}
                disabled={isLoading}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2 text-sm sm:text-base"
                data-testid="save-tags-button"
              >
                <Tag size={16} />
                <span>{isLoading ? '保存中...' : '画像タグの保存'}</span>
              </button>
              
              <button
                onClick={handleSaveEditedImage}
                disabled={isLoading}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2 text-sm sm:text-base"
                data-testid="save-image-button"
              >
                <Save size={16} />
                <span>{isLoading ? '保存中...' : '編集画像の保存'}</span>
              </button>
              
              <button
                onClick={handleDownloadImage}
                disabled={!imageUrl}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2 text-sm sm:text-base"
                data-testid="download-button"
              >
                <Download size={16} />
                <span>元画像ダウンロード</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Message Dialog */}
      <MessageDialog
        isOpen={showErrorDialog}
        onClose={closeErrorDialog}
        title={
          messageType === 'error' ? 'エラー' :
          messageType === 'success' ? '成功' :
          messageType === 'warning' ? '警告' :
          '情報'
        }
        message={errorMessage}
        type={messageType}
        buttonText="閉じる"
      />
    </div>
  )
}

export default ImageEditModal

